import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { SearchInput } from "../../../components/ui/search-input";
import { Label } from "../../../components/ui/label";
import { Select, SelectItem, SelectTrigger, SelectValue, SelectLabel } from "../../../components/ui/select";
import { useSelector } from "react-redux";
import { API_BASE_URL } from "../../../config/api";
import { SelectContent } from "@radix-ui/react-select";
import { Plus, Search, ChevronLeft, ChevronRight, Trash2, Edit2, Users, TrendingUp, Eye, EyeOff } from "lucide-react";
import { Loader } from "../../../components/Loader";
import { normalizeSearchQuery } from "../../../utils/search";

export const UsersPage = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [totalUsers, setTotalUsers] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [filterRole, setFilterRole] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [initialLoading, setInitialLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    id: "",
    role: "",
    username: "",
    password: "",
    mobile: "",
    email: "",
    companyName: "",
    address: "",
    gst: "",
    website: "",
  });
  
  const { authToken = "", id = "" } = useSelector(
    (state: any) => state.loginData || {}
  );

  const maxResults = 10;

  const str = (v: unknown) => (v == null ? "" : String(v));

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    const username = str(formData.username).trim();
    const mobile = str(formData.mobile).trim();
    const email = str(formData.email).trim();
    const password = str(formData.password);
    const address = str(formData.address).trim();
    const companyName = str(formData.companyName).trim();

    // Strict role validation - must be selected and not empty
    if (!formData.role || formData.role === "") {
      newErrors.role = "Role is required";
    }

    // Required field validations
    if (!username) newErrors.username = "Username is required";
    if (!mobile) newErrors.mobile = "Mobile number is required";

    // Email validation (optional; format only when provided)
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = "Invalid email format";
    }

    // Password required for login-capable roles on create only; blank on edit keeps current
    if (
      isCreating &&
      (formData.role === "admin" ||
        formData.role === "employee" ||
        formData.role === "dealer" ||
        formData.role === "interior")
    ) {
      if (!password.trim()) {
        newErrors.password = "Password is required";
      } else if (password.length < 4) {
        newErrors.password = "4 characters needed";
      }
    } else if (password && password.length > 0 && password.length < 4) {
      newErrors.password = "4 characters needed";
    }

    // Address / company required for dealer & interior only — optional for end-user
    if ((formData.role === "dealer" || formData.role === "interior") && !address) {
      newErrors.address = "Address is required";
    }

    if ((formData.role === "dealer" || formData.role === "interior") && !companyName) {
      newErrors.companyName = "Company name is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = () => {
    return (
      formData.role &&
      formData.role !== "" &&
      str(formData.username).trim() &&
      str(formData.mobile).trim()
    );
  };

  // Fetch users from API
  const fetchUsers = async (page = 1, search?: string) => {
    try {
      const firstResults = (page - 1) * maxResults;
      // Send "allroles" for the users management page to show all roles
      const roleParam = filterRole === "all" ? "allroles" : filterRole;
      const params: Record<string, unknown> = { firstResults, maxResults, role: roleParam };
      const q = normalizeSearchQuery(search ?? "");
      if (q) params.searchQuery = q;
      const response = await axios.get(`${API_BASE_URL}account/${id}/users`, {
        params,
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching users:", error);
      return { users: [], totalCount: 0 };
    }
  };

  // Create or Update user
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      if (formData.id) {
        await updateUser(formData.id, formData);
      } else {
        const newUser = await createUser(formData);
        setUsers([...users, newUser]);
      }
      const loadUsers = async () => {
        const result = await fetchUsers(page);
        setUsers(result.users);
        setTotalUsers(result.totalCount);
      };
      loadUsers();
      setIsDrawerOpen(false);
      resetForm();
    } catch (error: any) {
      const errorMsg = error.message || "Error saving user";
      setErrors({ submit: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  // Create user API call
  const createUser = async (userData: any) => {
    try {
      const response = await axios.post(
        `${API_BASE_URL}account/${id}/user/create`,
        userData,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "Error creating user";
      throw new Error(errorMessage);
    }
  };

  // Update user API call
  const updateUser = async (userId: string, userData: any) => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}account/${id}/user/update`,
        {
          id: Number(userId),
          role: userData.role,
          username: userData.username || "",
          password: userData.password || "",
          mobile: userData.mobile || "",
          email: userData.email || "",
          companyName: userData.companyName || "",
          address: userData.address || "",
          gst: userData.gst || "",
          website: userData.website || "",
        },
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || "Error updating user";
      throw new Error(errorMessage);
    }
  };

  // Delete user
  const handleDelete = async (userId: string) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await deleteUser(userId);
        setUsers(users.filter((user) => user.id !== userId));
      } catch (error) {
        console.error("Failed to delete user:", error);
      }
    }
  };

  // Delete user API call
  const deleteUser = async (userId: string) => {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}account/${id}/user/${userId}/delete`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      return response.data;
    } catch (error) {
      console.error("Error deleting user:", error);
      throw error;
    }
  };

  // Debounce search input → appliedSearch
  useEffect(() => {
    const t = setTimeout(() => {
      const q = normalizeSearchQuery(searchTerm);
      setAppliedSearch((prev) => {
        if (prev !== q) setPage(1);
        return q;
      });
    }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    const loadUsers = async () => {
      setTableLoading(true);
      try {
        const result = await fetchUsers(page, appliedSearch);
        setUsers(result.users || []);
        setTotalUsers(result.totalCount || 0);
      } finally {
        setInitialLoading(false);
        setTableLoading(false);
      }
    };
    loadUsers();
  }, [page, filterRole, appliedSearch]);

  // Open the drawer in create mode
  const openDrawerForCreate = () => {
    setFormData({
      id: "",
      role: "",
      username: "",
      password: "",
      mobile: "",
      email: "",
      address: "",
      companyName: "",
      gst: "",
      website: "",
    });
    setErrors({});
    setIsCreating(true);
    setIsDrawerOpen(true);
  };

  const fetchUserForEdit = async (userId: string | number) => {
    const res = await axios.get(`${API_BASE_URL}account/${id}/user/${userId}`, {
      headers: { Authorization: `Bearer ${authToken}` },
    });
    return res.data;
  };

  // Open the drawer in edit mode — load password from dedicated API (not list)
  const openDrawerForEdit = async (user: any) => {
    setErrors({});
    setIsCreating(false);
    setIsDrawerOpen(true);
    setEditLoading(true);
    try {
      const full = await fetchUserForEdit(user.id);
      setFormData({
        id: full?.id ?? user?.id ?? "",
        role: full?.role ?? user?.role ?? "",
        username: full?.username ?? user?.username ?? "",
        password: full?.password ?? "",
        mobile: full?.mobile ?? user?.mobile ?? "",
        email: full?.email ?? user?.email ?? "",
        companyName: full?.companyName ?? user?.companyName ?? "",
        address: full?.address ?? user?.address ?? "",
        gst: full?.gst ?? user?.gst ?? "",
        website: full?.website ?? user?.website ?? "",
      });
      setShowPassword(false);
    } catch (error) {
      console.error("Failed to load user for edit", error);
      setFormData({
        id: user?.id ?? "",
        role: user?.role ?? "",
        username: user?.username ?? "",
        password: "",
        mobile: user?.mobile ?? "",
        email: user?.email ?? "",
        companyName: user?.companyName ?? "",
        address: user?.address ?? "",
        gst: user?.gst ?? "",
        website: user?.website ?? "",
      });
      setErrors({ submit: "Could not load password. You can still edit other fields." });
      setShowPassword(false);
    } finally {
      setEditLoading(false);
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      id: "",
      role: "",
      username: "",
      password: "",
      mobile: "",
      email: "",
      address: "",
      companyName: "",
      gst: "",
      website: "",
    });
    setErrors({});
  };

  const filteredUsers = users;

  const totalPages = Math.max(1, Math.ceil((totalUsers || 0) / maxResults));
  const roleColors: any = {
    admin: "bg-purple-100 text-purple-800",
    dealer: "bg-blue-100 text-blue-800",
    interior: "bg-pink-100 text-pink-800",
    employee: "bg-green-100 text-green-800",
    "end-user": "bg-gray-100 text-gray-800",
  };

  if (initialLoading && users.length === 0) {
    return <Loader />;
  }

  return (
    <div className="space-y-6">
      {/* Modern Header with Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="md:col-span-2">
          <div>
            <h1 className="text-4xl font-bold text-gray-900 flex items-center gap-3">
              <Users className="text-purple-600" size={36} />
              Users
            </h1>
            <p className="text-gray-500 text-sm mt-1">Manage and create user accounts efficiently</p>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-4 border border-purple-200">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp size={16} className="text-purple-600" />
            <p className="text-xs font-semibold text-gray-600">Total Users</p>
          </div>
          <p className="text-2xl font-bold text-purple-900">{totalUsers}</p>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-4 border border-blue-200">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold text-gray-600">📊 Active</span>
          </div>
          <p className="text-2xl font-bold text-blue-900">{filteredUsers.length}/{totalUsers}</p>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-3.5 text-gray-400 pointer-events-none" size={20} />
          <SearchInput
            placeholder="Search by username, email, mobile, or code..."
            value={searchTerm}
            onValueChange={setSearchTerm}
            className="pl-12 py-3 border-2 border-gray-200 rounded-xl focus:border-purple-500 focus:ring-0 transition-colors"
          />
          {tableLoading && searchTerm.trim() ? (
            <span className="absolute right-3 top-3 text-xs font-medium text-purple-600">Searching…</span>
          ) : null}
        </div>

        <select
          className="px-4 py-3 border-2 border-gray-200 rounded-xl bg-white hover:border-purple-300 focus:outline-none focus:border-purple-500 transition-colors font-medium"
          onChange={(e) => { setFilterRole(e.target.value); setPage(1); }}
          value={filterRole || "all"}
        >
          <option value="all">All Roles</option>
          <option value="admin">👑 Admin</option>
          <option value="dealer">🏪 Dealer</option>
          <option value="employee">👔 Staff</option>
          <option value="interior">🎨 Interior</option>
          <option value="end-user">👤 End User</option>
        </select>

        <Button
          onClick={openDrawerForCreate}
          className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white flex items-center gap-2 py-3 px-6 rounded-xl shadow-lg shadow-purple-600/30"
        >
          <Plus size={20} />
          New User
        </Button>
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block relative">
        {tableLoading ? (
          <div className="absolute inset-0 z-20 bg-white/60 flex items-center justify-center pointer-events-none rounded-xl">
            <div className="h-8 w-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : null}
        {filteredUsers.length === 0 ? (
          <div className="bg-white rounded-xl border-2 border-gray-200 p-12 text-center">
            <Users size={48} className="mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium text-lg">No users found</p>
            <p className="text-gray-400 text-sm mt-1">Create a new user to get started</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border-2 border-gray-200 overflow-x-auto shadow-sm hover:shadow-md transition-shadow">
            <table className="w-full min-w-[720px]">
              <thead>
                <tr className="border-b border-gray-200 bg-gradient-to-r from-purple-50 to-purple-100">
                  <th className="px-4 py-4 text-left text-sm font-bold text-gray-900">Code</th>
                  <th className="px-4 py-4 text-left text-sm font-bold text-gray-900">Username</th>
                  <th className="px-4 py-4 text-left text-sm font-bold text-gray-900">Email</th>
                  <th className="px-4 py-4 text-left text-sm font-bold text-gray-900">Mobile</th>
                  <th className="px-4 py-4 text-left text-sm font-bold text-gray-900">Role</th>
                  <th className="px-4 py-4 text-right text-sm font-bold text-gray-900 sticky right-0 bg-gradient-to-r from-purple-50 to-purple-100 shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.08)]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr
                    key={user.id}
                    className="border-b border-gray-100 hover:bg-purple-50 transition-colors group"
                  >
                    <td className="px-4 py-4 text-sm font-semibold text-purple-700">
                      {user.userCode || "—"}
                    </td>
                    <td className="px-4 py-4 text-sm font-medium text-gray-900 group-hover:text-purple-700">
                      {user.username || "-"}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-700 max-w-[180px] truncate" title={user.email || ""}>
                      {user.email || "-"}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600 whitespace-nowrap">{user.mobile || "-"}</td>
                    <td className="px-4 py-4 text-sm">
                      <span
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold inline-flex items-center gap-1 ${
                          roleColors[user.role] || "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {user.role === "admin" && "👑"}
                        {user.role === "dealer" && "🏪"}
                        {user.role === "employee" && "👔"}
                        {user.role === "interior" && "🎨"}
                        {user.role === "end-user" && "👤"}
                        {user.role === "employee" ? "Staff" : user.role || "-"}
                      </span>
                    </td>
                    <td className="px-4 py-4 text-right space-x-2 whitespace-nowrap sticky right-0 bg-white group-hover:bg-purple-50 shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.06)]">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => void openDrawerForEdit(user)}
                        className="text-purple-600 border-purple-300 hover:bg-purple-50 hover:border-purple-400 font-medium"
                      >
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(user.id)}
                        className="text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400 font-medium"
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-4">
        {filteredUsers.length === 0 ? (
          <div className="p-8 text-center text-gray-500 bg-white rounded-lg border border-gray-200">
            <p>No users found</p>
          </div>
        ) : (
          filteredUsers.map((user) => (
            <div
              key={user.id}
              className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-start mb-3">
                <div>
                  <p className="font-semibold text-gray-900">{user.username || "N/A"}</p>
                  {user.userCode && (
                    <p className="text-xs font-semibold text-purple-700 mt-0.5">{user.userCode}</p>
                  )}
                  <p className="text-xs text-gray-500">{user.email}</p>
                </div>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    roleColors[user.role] || "bg-gray-100 text-gray-800"
                  }`}
                >
                  {user.role}
                </span>
              </div>
              <div className="space-y-2 text-sm mb-4">
                {user.userCode && (
                  <p className="text-gray-600">
                    <span className="font-medium">Code:</span> {user.userCode}
                  </p>
                )}
                <p className="text-gray-600">
                  <span className="font-medium">Email:</span> {user.email || "-"}
                </p>
                <p className="text-gray-600">
                  <span className="font-medium">Mobile:</span> {user.mobile || "-"}
                </p>
                {user.companyName && (
                  <p className="text-gray-600">
                    <span className="font-medium">Company:</span> {user.companyName}
                  </p>
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => void openDrawerForEdit(user)}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Edit2 size={16} className="mr-2" />
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => handleDelete(user.id)}
                  className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                >
                  <Trash2 size={16} className="mr-2" />
                  Delete
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 p-4 bg-white rounded-lg border border-gray-200">
          <p className="text-sm text-gray-600">
            Page <strong>{page}</strong> of <strong>{totalPages}</strong> ({<strong>{totalUsers}</strong>} total users)
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="flex items-center gap-2 disabled:opacity-50"
            >
              <ChevronLeft size={18} />
              <span className="hidden sm:inline">Previous</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="flex items-center gap-2 disabled:opacity-50"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight size={18} />
            </Button>
          </div>
        </div>
      )}

      {/* Drawer Overlay */}
      {isDrawerOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsDrawerOpen(false)}
        />
      )}

      {/* Drawer Form */}
      <div
        className={`fixed md:left-1/2 md:top-1/2 md:transform md:-translate-x-1/2 md:-translate-y-1/2 bottom-0 left-0 right-0 md:bottom-auto bg-white z-50 transform transition-all duration-300 w-full md:w-96 md:h-auto max-h-screen md:max-h-[90vh] overflow-y-auto rounded-t-2xl md:rounded-xl border-2 md:border-2 border-gray-200 md:border-purple-200 shadow-2xl ${
          isDrawerOpen
            ? "translate-y-0 md:scale-100 md:opacity-100"
            : "translate-y-full md:scale-0 md:opacity-0 pointer-events-none"
        }`}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 text-white px-6 py-4 sticky top-0 z-10 flex justify-between items-center rounded-t-2xl md:rounded-t-xl">
          <div>
            <h2 className="text-xl font-bold">
              {isCreating ? "➕ New User" : "✏️ Edit User"}
            </h2>
            <p className="text-xs text-purple-100 mt-1">
              {isCreating ? "Add a new team member" : "Update user details"}
            </p>
          </div>
          <button
            onClick={() => setIsDrawerOpen(false)}
            className="text-white hover:bg-purple-500 p-2 rounded-lg transition-colors"
          >
            ✕
          </button>
        </div>

        {/* Form Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {editLoading && !isCreating && (
            <div className="flex items-center gap-2 text-sm text-purple-700 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2">
              <div className="h-4 w-4 border-2 border-purple-500 border-t-transparent rounded-full animate-spin shrink-0" />
              Loading user details…
            </div>
          )}
          {errors.submit && (
            <div className="bg-red-50 border-2 border-red-300 rounded-lg p-3">
              <p className="text-red-700 font-semibold text-sm">{errors.submit}</p>
            </div>
          )}

          {/* Basic Info Section */}
          <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4 space-y-4">
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <span>👤</span> Personal Information
            </h3>

            <div>
              <Label className="text-gray-700 font-semibold flex items-center gap-1">
                <span>�</span> Role
              </Label>
              <select
                value={formData.role || ""}
                onChange={(e) => {
                  setFormData({ ...formData, role: e.target.value });
                  setErrors({ ...errors, role: "" });
                }}
                className={`w-full mt-2 px-4 py-2.5 border-2 rounded-lg focus:outline-none focus:ring-1 focus:ring-purple-300 font-medium bg-white ${
                  errors.role ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-purple-500"
                }`}
              >
                <option value="">Select Role</option>
                <option value="admin">👑 Admin</option>
                <option value="employee">👔 Staff</option>
                <option value="dealer">🏪 Dealer</option>
                <option value="interior">🎨 Interior Designer</option>
                <option value="end-user">👤 End User</option>
              </select>
              {errors.role && <p className="text-red-600 text-xs mt-1">{errors.role}</p>}
            </div>

            <div>
              <Label className="text-gray-700 font-semibold flex items-center gap-1">
                <span>👤</span> Username
              </Label>
              <Input
                value={formData.username || ""}
                onChange={(e) => {
                  setFormData({ ...formData, username: e.target.value });
                  setErrors({ ...errors, username: "" });
                }}
                placeholder="Enter username"
                className={`mt-2 border-2 rounded-lg focus:ring-0 py-2.5 ${
                  errors.username ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-purple-500"
                }`}
                required
              />
              {errors.username && <p className="text-red-600 text-xs mt-1">{errors.username}</p>}
            </div>

            <div>
              <Label className="text-gray-700 font-semibold flex items-center gap-1">
                <span>📱</span> Mobile Number
              </Label>
              <Input
                type="tel"
                inputMode="numeric"
                value={formData.mobile || ""}
                onChange={(e) => {
                  // Only allow numbers
                  const numbersOnly = e.target.value.replace(/[^0-9]/g, "");
                  setFormData({ ...formData, mobile: numbersOnly });
                  setErrors({ ...errors, mobile: "" });
                }}
                placeholder="10 digit number (without country code)"
                className={`mt-2 border-2 rounded-lg focus:ring-0 py-2.5 ${
                  errors.mobile ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-purple-500"
                }`}
                required
              />
              {errors.mobile && <p className="text-red-600 text-xs mt-1">{errors.mobile}</p>}
            </div>

            <div>
              <Label className="text-gray-700 font-semibold flex items-center gap-1">
                <span>📧</span> Email
              </Label>
              <Input
                type="email"
                value={formData.email || ""}
                onChange={(e) => {
                  setFormData({ ...formData, email: e.target.value });
                  setErrors({ ...errors, email: "" });
                }}
                placeholder="Enter email address (optional)"
                className={`mt-2 border-2 rounded-lg focus:ring-0 py-2.5 ${
                  errors.email ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-purple-500"
                }`}
              />
              {errors.email && <p className="text-red-600 text-xs mt-1">{errors.email}</p>}
            </div>
          </div>

          {/* Company Info Section */}
          {(formData.role === "dealer" || formData.role === "interior" || formData.role === "end-user") && (
            <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 space-y-4">
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                <span>🏢</span> Company Information
              </h3>

              <div>
                <Label className="text-gray-700 font-semibold flex items-center gap-1">
                  <span>🏪</span> Company Name
                  {(formData.role === "dealer" || formData.role === "interior") ? " *" : " (optional)"}
                </Label>
                <Input
                  value={formData.companyName || ""}
                  onChange={(e) => {
                    setFormData({ ...formData, companyName: e.target.value });
                    setErrors({ ...errors, companyName: "" });
                  }}
                  placeholder="Enter company name"
                  className={`mt-2 border-2 rounded-lg focus:ring-0 py-2.5 ${
                    errors.companyName ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-blue-500"
                  }`}
                />
                {errors.companyName && <p className="text-red-600 text-xs mt-1">{errors.companyName}</p>}
              </div>

              <div>
                <Label className="text-gray-700 font-semibold flex items-center gap-1">
                  <span>📍</span> Address
                  {(formData.role === "dealer" || formData.role === "interior") ? " *" : " (optional)"}
                </Label>
                <Input
                  value={formData.address || ""}
                  onChange={(e) => {
                    setFormData({ ...formData, address: e.target.value });
                    setErrors({ ...errors, address: "" });
                  }}
                  placeholder="Enter address"
                  className={`mt-2 border-2 rounded-lg focus:ring-0 py-2.5 ${
                    errors.address ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-blue-500"
                  }`}
                />
                {errors.address && <p className="text-red-600 text-xs mt-1">{errors.address}</p>}
              </div>

              <div>
                <Label className="text-gray-700 font-semibold flex items-center gap-1">
                  <span>📋</span> GST Number
                </Label>
                <Input
                  value={formData.gst || ""}
                  onChange={(e) => setFormData({ ...formData, gst: e.target.value })}
                  placeholder="Enter GST number"
                  className="mt-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-0 py-2.5"
                />
              </div>

              <div>
                <Label className="text-gray-700 font-semibold flex items-center gap-1">
                  <span>🌐</span> Website
                </Label>
                <Input
                  value={formData.website || ""}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  placeholder="Enter website URL"
                  className="mt-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-0 py-2.5"
                />
              </div>
            </div>
          )}

          {/* Security Section */}
          {(formData.role === "admin" || formData.role === "employee" || formData.role === "dealer" || formData.role === "interior") && (
            <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-4">
              <Label className="text-gray-700 font-semibold flex items-center gap-1">
                <span>🔐</span> Password
              </Label>
              <div className="relative mt-3">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={formData.password || ""}
                  onChange={(e) => {
                    setFormData({ ...formData, password: e.target.value });
                    setErrors({ ...errors, password: "" });
                  }}
                  placeholder="Enter password"
                  className={`border-2 rounded-lg focus:ring-0 py-2.5 pr-10 ${
                    errors.password ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-orange-500"
                  }`}
                  required={isCreating}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && <p className="text-red-600 text-xs mt-2">{errors.password}</p>}
              <p className="text-xs text-orange-600 mt-2">
                {isCreating
                  ? "Required for new users (min 4 characters)"
                  : "Password is hidden by default. Use the eye icon to view. Change only if updating; save without changes to keep the current password."}
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t-2 border-gray-200">
            <Button
              type="submit"
              disabled={loading || !isFormValid()}
              className="flex-1 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white font-bold py-2.5 rounded-lg shadow-lg shadow-purple-600/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (isCreating ? "Creating..." : "Updating...") : (isCreating ? "✓ Create User" : "✓ Update User")}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsDrawerOpen(false)}
              className="flex-1 border-2 border-gray-300 text-gray-700 font-bold hover:bg-gray-50 py-2.5 rounded-lg"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UsersPage;
